import { parseAddTownCompanyRequestStatus } from './add-town-company-request-status'
import type { ProjectStatus } from './project-status'

/** 「データ登録中へ戻す」の取り消し（回答受付フェーズへ復元）が可能な進行状況 */
export const RESTORE_RESPONSE_PHASE_SOURCE_STATUSES: readonly ProjectStatus[] = [
  'draft_list',
  'operator_processing',
  'waiting_for_approval',
] as const

export type RestoreResponsePhaseTargetStatus = 'collecting_responses' | 'collection_closed'

export type TownCompanyForRestoreCheck = {
  sendRequestNumber?: string | null
}

export type RequestForRestoreCheck = {
  status: string
  sendRequestNumber?: string | null
}

/**
 * 本郵送回で追加した（郵送回未設定の）事業所が残っているか。
 */
export const hasUnsentAddedTownCompanies = (
  townCompanies: TownCompanyForRestoreCheck[],
): boolean => {
  return townCompanies.some((tc) => !tc.sendRequestNumber)
}

/**
 * 未対応（対応中 / 未対応）かつ郵送回未設定のデータ生成リクエストがあるか。
 * 完了・キャンセル済みは「対応済み」とみなす。
 */
export const hasUnhandledUnboundDataGenRequests = (
  requests: RequestForRestoreCheck[],
): boolean => {
  return requests.some((request) => {
    if (request.sendRequestNumber) return false
    const status = parseAddTownCompanyRequestStatus(request.status)
    return status === 'in_progress' || status === 'pending'
  })
}

/**
 * form.closed から復元先の進行状況を決定する。
 * 「データ登録中へ戻す」は form.closed を変更しないため、これが元の受付状態の正とする。
 */
export const resolveRestoreResponsePhaseTargetStatus = (
  formClosed: boolean,
): RestoreResponsePhaseTargetStatus => {
  return formClosed ? 'collection_closed' : 'collecting_responses'
}

/**
 * 回答受付フェーズへ復元できるか（UI・サービス共通の前提条件）。
 */
export const canRestoreResponsePhaseFromDraftList = (params: {
  projectStatus: ProjectStatus | null | undefined
  hasSurveyForm: boolean
  townCompanies: TownCompanyForRestoreCheck[]
  requests: RequestForRestoreCheck[]
}): boolean => {
  const { projectStatus, hasSurveyForm, townCompanies, requests } = params
  if (!projectStatus) return false
  if (!(RESTORE_RESPONSE_PHASE_SOURCE_STATUSES as readonly string[]).includes(projectStatus)) {
    return false
  }
  if (!hasSurveyForm) return false
  if (hasUnsentAddedTownCompanies(townCompanies)) return false
  if (hasUnhandledUnboundDataGenRequests(requests)) return false
  return true
}
